import { apiGet, apiPost, apiPatch, apiDelete } from '@/utils/api';

export type MarketingLeadSource =
  | 'MANUAL'
  | 'CSV_IMPORT'
  | 'FORMER_USER'
  | 'API'
  | 'PUBLIC_FORM';

export type MarketingLeadStatus = 'ACTIVE' | 'UNSUBSCRIBED' | 'BOUNCED';
export type MarketingLeadFieldType = 'TEXT' | 'NUMBER' | 'DATE' | 'BOOLEAN' | 'SELECT';

export interface MarketingLead {
  id: string;
  email: string;
  firstName?: string | null;
  lastName?: string | null;
  fullName?: string | null;
  phone?: string | null;
  company?: string | null;
  jobTitle?: string | null;
  city?: string | null;
  country?: string | null;
  source: MarketingLeadSource;
  tags: string[];
  customFields: Record<string, unknown>;
  formerUserId?: string | null;
  formerRole?: string | null;
  status: MarketingLeadStatus;
  unsubscribedAt?: string | null;
  bouncedAt?: string | null;
  lastEmailedAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface MarketingLeadField {
  id: string;
  key: string;
  label: string;
  type: MarketingLeadFieldType;
  options?: string[] | null;
  required: boolean;
  isActive: boolean;
  sortOrder: number;
}

export interface CreateLeadDto {
  email: string;
  firstName?: string;
  lastName?: string;
  fullName?: string;
  phone?: string;
  company?: string;
  jobTitle?: string;
  city?: string;
  country?: string;
  tags?: string[];
  customFields?: Record<string, unknown>;
}

export interface UpdateLeadDto extends Partial<CreateLeadDto> {
  status?: MarketingLeadStatus;
}

export interface LeadFilters {
  search?: string;
  status?: MarketingLeadStatus;
  source?: MarketingLeadSource;
  tags?: string[];
  page?: number;
  limit?: number;
}

export interface LeadListResponse {
  data: MarketingLead[];
  meta: { page: number; limit: number; total: number; totalPages: number };
}

export interface LeadStats {
  total: number;
  active: number;
  unsubscribed: number;
  formerUsers: number;
}

export interface ImportRow {
  email: string;
  firstName?: string;
  lastName?: string;
  fullName?: string;
  phone?: string;
  company?: string;
  jobTitle?: string;
  city?: string;
  country?: string;
  customFields?: Record<string, unknown>;
}

export interface ImportLeadsDto {
  rows: ImportRow[];
  fileName?: string;
  mapping?: Record<string, string>;
  tags?: string[];
}

export interface ImportResult {
  batchId: string;
  totalRows: number;
  importedRows: number;
  skippedAsUser: number;
  skippedAsDuplicate: number;
  errorRows: number;
  errors: Array<{ row: number; message: string }>;
}

// ---- Lead CRUD ----
export const listLeads = (filters: LeadFilters = {}) => {
  const params = new URLSearchParams();
  if (filters.search) params.append('search', filters.search);
  if (filters.status) params.append('status', filters.status);
  if (filters.source) params.append('source', filters.source);
  if (filters.tags && filters.tags.length > 0) params.append('tags', filters.tags.join(','));
  if (filters.page) params.append('page', String(filters.page));
  if (filters.limit) params.append('limit', String(filters.limit));
  const query = params.toString() ? `?${params}` : '';
  return apiGet<LeadListResponse>(`/marketing-leads${query}`);
};

export const getLead = (id: string) => apiGet<MarketingLead>(`/marketing-leads/${id}`);
export const createLead = (data: CreateLeadDto) =>
  apiPost<MarketingLead>('/marketing-leads', data);
export const updateLead = (id: string, data: UpdateLeadDto) =>
  apiPatch<MarketingLead>(`/marketing-leads/${id}`, data);
export const deleteLead = (id: string) => apiDelete<void>(`/marketing-leads/${id}`);

export const getLeadStats = () => apiGet<LeadStats>('/marketing-leads/stats');

export const listLeadTags = () =>
  apiGet<{ tag: string; count: number }[]>('/marketing-leads/tags');

export const importLeads = (data: ImportLeadsDto) =>
  apiPost<ImportResult>('/marketing-leads/import', data);

export const importFormerUsers = () =>
  apiPost<{ candidates: number; imported: number; skipped: number }>(
    '/marketing-leads/import-former-users',
    {},
  );

// ---- Field definitions ----
export const listFields = (activeOnly = false) =>
  apiGet<MarketingLeadField[]>(
    `/marketing-leads/fields${activeOnly ? '?activeOnly=true' : ''}`,
  );
export const createField = (data: {
  key: string;
  label: string;
  type?: MarketingLeadFieldType;
  options?: string[];
  required?: boolean;
  sortOrder?: number;
}) => apiPost<MarketingLeadField>('/marketing-leads/fields', data);
export const updateField = (
  id: string,
  data: Partial<{
    label: string;
    type: MarketingLeadFieldType;
    options: string[];
    required: boolean;
    isActive: boolean;
    sortOrder: number;
  }>,
) => apiPatch<MarketingLeadField>(`/marketing-leads/fields/${id}`, data);
export const removeField = (id: string) =>
  apiDelete<MarketingLeadField>(`/marketing-leads/fields/${id}`);

// ---- Helpers ----
export const sourceLabel = (s: MarketingLeadSource): string => {
  switch (s) {
    case 'MANUAL': return 'Ajouté manuellement';
    case 'CSV_IMPORT': return 'Importé (Excel/CSV)';
    case 'FORMER_USER': return 'Ancien utilisateur';
    case 'API': return 'API';
    case 'PUBLIC_FORM': return 'Formulaire public';
    default: return s;
  }
};

export const statusLabel = (s: MarketingLeadStatus): string => {
  switch (s) {
    case 'ACTIVE': return 'Actif';
    case 'UNSUBSCRIBED': return 'Désinscrit';
    case 'BOUNCED': return 'Email invalide';
    default: return s;
  }
};

export const statusColor = (s: MarketingLeadStatus): string => {
  switch (s) {
    case 'ACTIVE': return 'bg-green-100 text-green-800';
    case 'UNSUBSCRIBED': return 'bg-gray-200 text-gray-700';
    case 'BOUNCED': return 'bg-red-100 text-red-800';
    default: return 'bg-gray-100 text-gray-700';
  }
};
